import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Radar as RadarIcon } from "lucide-react";
import { api } from "@/lib/api";
import { CoverImage } from "@/components/CoverImage";
import { hasUpcomingDate, nextUpcomingDate } from "@/lib/events";
import { prefetchArticle, prefetchSong, prefetchPlaylist, prefetchArtist } from "@/lib/prefetch";

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" }); }
  catch (e) { return ""; }
};

function PanelHeader({ eyebrow, title, to, label, onNavigate }) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4">
      <div>
        <p className="eyebrow mb-1.5">{eyebrow}</p>
        <h3 className="display text-2xl md:text-3xl leading-none">{title}</h3>
      </div>
      <Link to={to} onClick={onNavigate} className="group inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-5 py-2.5 text-white bg-gradient-to-r from-[#2A00D9] to-[#4b1fe0] shadow-md shadow-[#2A00D9]/25 hover:gap-2.5 hover:shadow-lg hover:shadow-[#2A00D9]/30 transition-all whitespace-nowrap" data-testid={`mega-see-all-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        {label} <ArrowRight size={16} className="transition-transform" />
      </Link>
    </div>
  );
}

export function MegaMenuPanel({ active, onNavigate }) {
  const [data, setData] = useState({ news: [], songs: [], radar: [], events: [], playlists: [] });

  useEffect(() => {
    let ok = true;
    Promise.all([
      api.get("/articles").then((r) => r.data).catch(() => []),
      api.get("/songs").then((r) => r.data).catch(() => []),
      api.get("/radar").then((r) => r.data).catch(() => ({})),
      api.get("/events").then((r) => r.data).catch(() => []),
      api.get("/playlists").then((r) => r.data).catch(() => []),
    ]).then(([articles, songs, radar, events, playlists]) => {
      if (!ok) return;
      const radarArts = [radar?.featured, ...(radar?.watching || []), ...(radar?.growing || [])].filter(Boolean);
      const seen = new Set();
      const radarUniq = radarArts.filter((a) => (seen.has(a.id) ? false : seen.add(a.id)));
      const upcoming = (events || [])
        .filter(hasUpcomingDate)
        .sort((a, b) => {
          const am = (a.city || "").toLowerCase().includes("madrid") ? 0 : 1;
          const bm = (b.city || "").toLowerCase().includes("madrid") ? 0 : 1;
          if (am !== bm) return am - bm;
          return new Date(nextUpcomingDate(a)) - new Date(nextUpcomingDate(b));
        });
      const sortedSongs = [...(songs || [])].filter((s) => s.published !== false)
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      const sortedPl = [...(playlists || [])].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      setData({
        news: (articles || []).slice(0, 3),
        songs: sortedSongs.slice(0, 4),
        radar: radarUniq.slice(0, 4),
        events: upcoming.slice(0, 4),
        playlists: sortedPl.slice(0, 3),
      });
    });
    return () => { ok = false; };
  }, []);

  const renderBody = () => {
    if (active === "news") {
      return (
        <>
          <PanelHeader eyebrow="Lo último" title="Noticias" to="/noticias" label="Ver todas las noticias" onNavigate={onNavigate} />
          <div className="grid grid-cols-3 gap-6">
            {data.news.map((a) => (
              <Link to={`/noticias/${a.slug}`} key={a.id} onClick={onNavigate} onMouseEnter={() => prefetchArticle(a.slug)} className="group" data-testid={`mega-news-${a.slug}`}>
                <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[#F3F2EE] mb-3">
                  <CoverImage src={a.cover_image} alt={a.cover_image_alt || a.title} ratio="16/10" sizes="420px" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="eyebrow mb-1">{a.format || "Noticia"}{a.published_at ? ` · ${fmtDate(a.published_at)}` : ""}</p>
                <h4 className="display text-lg leading-tight line-clamp-2 group-hover:text-[#2A00D9] transition-colors">{a.title}</h4>
              </Link>
            ))}
          </div>
        </>
      );
    }
    if (active === "songs") {
      return (
        <>
          <PanelHeader eyebrow="El significado detrás" title="Canciones" to="/canciones" label="Ver todas las canciones" onNavigate={onNavigate} />
          <div className="grid grid-cols-4 gap-6">
            {data.songs.map((s) => (
              <Link to={`/canciones/${s.slug}`} key={s.id} onClick={onNavigate} onMouseEnter={() => prefetchSong(s.slug)} className="group" data-testid={`mega-song-${s.slug}`}>
                <div className="aspect-square rounded-xl overflow-hidden bg-[#F3F2EE] mb-3">
                  <CoverImage src={s.cover_image} alt={s.cover_image_alt || s.song} ratio="1/1" sizes="300px" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h4 className="display text-base leading-tight line-clamp-1 group-hover:text-[#2A00D9] transition-colors">{s.song}</h4>
                <p className="text-sm text-[#4A4A4A] line-clamp-1">{s.artist}</p>
              </Link>
            ))}
          </div>
        </>
      );
    }
    if (active === "radar") {
      return (
        <>
          <PanelHeader eyebrow="Descubrimiento" title="Radar" to="/radar" label="Ver Radar" onNavigate={onNavigate} />
          <div className="grid grid-cols-4 gap-6">
            {data.radar.map((ar) => (
              <Link to={`/radar/${ar.slug}`} key={ar.id} onClick={onNavigate} onMouseEnter={() => prefetchArtist(ar.slug)} className="group relative rounded-2xl overflow-hidden bg-[#0A0A0A] aspect-[3/4]" data-testid={`mega-radar-${ar.slug}`}>
                <CoverImage src={ar.image} alt={ar.image_alt || ar.name} ratio="3/4" sizes="300px" className="absolute inset-0 w-full h-full object-cover opacity-80 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B4D844] mb-1 inline-flex items-center gap-1"><RadarIcon size={11} /> {ar.genre || "Radar"}</p>
                  <h4 className="text-white font-bold text-lg leading-none">{ar.name}</h4>
                </div>
              </Link>
            ))}
          </div>
        </>
      );
    }
    if (active === "agenda") {
      return (
        <>
          <PanelHeader eyebrow="Próximos directos · Madrid" title="Agenda" to="/agenda" label="Ver agenda" onNavigate={onNavigate} />
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {data.events.map((e) => {
              const row = (
                <>
                  <div className="shrink-0 w-14 text-center">
                    <span className="block display text-xl leading-none text-[#2A00D9]">{new Date(nextUpcomingDate(e)).getDate() || "–"}</span>
                    <span className="block text-[10px] uppercase tracking-wide text-[#4A4A4A]">{new Date(nextUpcomingDate(e)).toLocaleDateString("es-ES", { month: "short" })}</span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="display text-base leading-tight line-clamp-1 group-hover:text-[#2A00D9] transition-colors">{e.artist || e.title}</h4>
                    <p className="text-sm text-[#4A4A4A] line-clamp-1 inline-flex items-center gap-1"><MapPin size={12} /> {e.venue}{e.city ? `, ${e.city}` : ""}</p>
                  </div>
                </>
              );
              const cls = "group flex items-center gap-4 py-2 border-b border-black/5 last:border-0";
              return e.ticket_url
                ? <a href={e.ticket_url} target="_blank" rel="noreferrer" key={e.id} onClick={onNavigate} className={cls} data-testid={`mega-event-${e.id}`}>{row}</a>
                : <Link to="/agenda" key={e.id} onClick={onNavigate} className={cls} data-testid={`mega-event-${e.id}`}>{row}</Link>;
            })}
          </div>
        </>
      );
    }
    if (active === "playlists") {
      return (
        <>
          <PanelHeader eyebrow="Para tus oídos" title="Playlists" to="/playlists" label="Ver playlists" onNavigate={onNavigate} />
          <div className="grid grid-cols-3 gap-6">
            {data.playlists.map((p) => (
              <Link to={`/playlists/${p.slug}`} key={p.id} onClick={onNavigate} onMouseEnter={() => prefetchPlaylist(p.slug)} className="group flex gap-4" data-testid={`mega-playlist-${p.slug}`}>
                <div className="shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-[#F3F2EE]">
                  <CoverImage src={p.cover_image} alt={p.title} ratio="1/1" sizes="200px" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="min-w-0 pt-1">
                  <h4 className="display text-lg leading-tight line-clamp-1 group-hover:text-[#2A00D9] transition-colors">{p.title}</h4>
                  {p.description && <p className="text-sm text-[#4A4A4A] line-clamp-2 mt-1">{p.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        </>
      );
    }
    return null;
  };

  if (!active || active === "inicio") return null;
  return (
    <div
      className="anim-panel-down hidden md:block absolute left-0 right-0 top-full overflow-hidden bg-gradient-to-b from-white via-white to-[#FAF9F6] border-t border-black/5 shadow-[0_28px_44px_-26px_rgba(0,0,0,0.35)]"
      data-testid="mega-panel"
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#2A00D9] via-[#B4D844] to-[#2A00D9]" aria-hidden />
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#B4D844]/25 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#2A00D9]/10 blur-3xl" aria-hidden />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 py-9">
        <div key={active} className="anim-fade-in">
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
